import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { AUTH_USER_CHANGED_EVENT, authService } from '../services/auth';
import { canReadTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';

interface PrivateRouteProps {
  children: React.ReactNode;
  roles?: string[];
  requireMargin?: boolean;
  requirePurchaseInvoices?: boolean;
  requireDocumentCenter?: boolean;
  requireSupplierOrders?: boolean;
  requireSalesDocuments?: boolean;
  requireFinancialReports?: boolean;
  requireWarehouseDocuments?: boolean;
  requireSupplierPurchasePrices?: boolean;
  requireTransportRoutes?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  roles,
  requireMargin = false,
  requirePurchaseInvoices = false,
  requireDocumentCenter = false,
  requireSupplierOrders = false,
  requireSalesDocuments = false,
  requireFinancialReports = false,
  requireWarehouseDocuments = false,
  requireSupplierPurchasePrices = false,
  requireTransportRoutes = false,
}) => {
  const [, setAuthRevision] = useState(0);
  const [verificationComplete, setVerificationComplete] = useState(!authService.isAuthenticated());
  const [verificationValid, setVerificationValid] = useState<boolean | null>(null);
  const isAuthenticated = authService.isAuthenticated();
  const hasRequiredRole = !roles || authService.hasRole(roles);
  const hasRequiredMarginVisibility = !requireMargin || authService.canViewMargin();
  const hasRequiredCapabilities = !requirePurchaseInvoices || authService.canManagePurchaseInvoices();
  const hasRequiredDocumentCenterVisibility = !requireDocumentCenter || authService.canViewDocumentCenter();
  const hasRequiredSupplierOrderVisibility = !requireSupplierOrders || authService.canManageSupplierOrders();
  const hasRequiredSalesDocumentVisibility = !requireSalesDocuments || authService.canViewSalesDocuments();
  const hasRequiredFinancialReportVisibility = !requireFinancialReports || authService.canViewFinancialReports();
  const hasRequiredWarehouseDocumentVisibility = !requireWarehouseDocuments || authService.canViewWarehouseDocuments();
  const hasRequiredSupplierPurchasePriceVisibility = !requireSupplierPurchasePrices || authService.canViewSupplierPurchasePrices();
  const hasRequiredTransportRouteVisibility = !requireTransportRoutes
    || canReadTransportRoutes(authService.getCurrentUser());
  const location = useLocation();

  useEffect(() => {
    const handleAuthUserChanged = () => setAuthRevision((revision) => revision + 1);
    window.addEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setVerificationComplete(true);
      setVerificationValid(false);
      return;
    }

    let cancelled = false;
    setVerificationComplete(false);
    authService.verifyToken()
      .then((result) => {
        if (!cancelled) {
          setVerificationValid(result?.valid === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVerificationValid(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setVerificationComplete(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!verificationComplete) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (verificationValid === false) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (
    !hasRequiredRole
    || !hasRequiredMarginVisibility
    || !hasRequiredCapabilities
    || !hasRequiredDocumentCenterVisibility
    || !hasRequiredSupplierOrderVisibility
    || !hasRequiredSalesDocumentVisibility
    || !hasRequiredFinancialReportVisibility
    || !hasRequiredWarehouseDocumentVisibility
    || !hasRequiredSupplierPurchasePriceVisibility
    || !hasRequiredTransportRouteVisibility
  ) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
